<template>
  <v-card flat color="transparent">
    <v-card-text class="bg-transparent px-3" :class="paddingRight ? 'pr-1' : 'pl-1'">
      <!-- Conditionally rendered trip stats section if a trip is selected. -->
      <v-card flat v-if="displayedComparisonData" color="transparent" class="px-1">
        <!-- vehicle section -->
        <v-card
          class="pt-3 ma-1 mb-3 d-flex flex-column rounded-lg"
          elevation="3"
          color="transparent"
        >
          <v-row no-gutters class="align-center">
            <v-col cols="4" class="pl-2">
              <v-img
                :lazy-src="getSrc('car_images/placeholder_car.png')"
                :src="getImageSrc()"
                contain
              />
            </v-col>
            <v-col cols="8" style="min-height: 70px">
              <v-card-title class="pb-0">
                {{ vehicle?.licensePlate ? vehicle.licensePlate : 'no plate' }}
              </v-card-title>
              <v-card-subtitle class="pb-0">
                {{ vehicle?.evModel ? vehicle.evModel.name : 'model not specified' }}
              </v-card-subtitle>
            </v-col>
          </v-row>
        </v-card>
        <!-- Performance -->
        <v-card class="rounded-lg ma-1 mb-3" color="transparent">
          <v-card-title style="font-size: 16px">Performance summary</v-card-title>
          <v-card-text>
            <v-row no-gutters class="mb-3">
              <v-icon color="primary" class="mr-1"> mdi-clock-outline </v-icon>
              {{ drivingTime }}
              driving time
            </v-row>
            <v-row no-gutters>
              <v-icon color="primary" class="mr-1"> mdi-map-marker-radius-outline </v-icon>
              {{ drivingDistance }}
              km distance travelled
            </v-row>
          </v-card-text>
          <v-card-title class="text-body-1 font-weight-medium"> Charging time </v-card-title>
          <v-card-text>
            <v-card
              v-if="chargingTimeToUse === 'DC' || chargingTimeToUse === 'AC DC split'"
              class="bg-background pa-2 flex-grow-1 rounded-lg"
              flat
            >
              <v-row no-gutters>
                <span class="text-primary mr-1 font-weight-bold">DC</span>
                <span class="font-weight-regular">
                  {{ dcChargingTime }}
                </span>
              </v-row>
            </v-card>
            <v-card
              v-if="chargingTimeToUse === 'AC' || chargingTimeToUse === 'AC DC split'"
              class="bg-background pa-2 flex-grow-1 rounded-lg mb-2"
              flat
            >
              <v-row no-gutters>
                <span class="text-primary mr-1 font-weight-bold">AC</span>
                <span class="font-weight-regular">
                  {{ acChargingTime }}
                </span>
              </v-row>
            </v-card>
            <v-card
              v-if="chargingTimeToUse === 'H'"
              class="bg-background pa-2 flex-grow-1 rounded-lg"
              flat
            >
              <v-row no-gutters>
                <span class="text-primary mr-1 font-weight-bold">H₂</span>
                <span class="font-weight-regular">
                  {{ HydrogenChargingTime }}
                </span>
              </v-row>
            </v-card>
          </v-card-text>
          <v-card-title class="text-body-1 font-weight-medium"> Battery usage </v-card-title>
          <v-card-text>
            <v-row no-gutters class="mb-3">
              <v-card class="bg-background pa-2 rounded-lg flex-grow-1 text-caption" flat>
                <v-icon color="primary"> mdi-ev-station </v-icon>
                {{ numberOfChargingStopsNeeded }}
              </v-card>
            </v-row>
            <v-row no-gutters class="mb-3">
              <v-card class="bg-background pa-2 rounded-lg flex-grow-1 text-caption" flat>
                <v-icon color="primary"> mdi-battery-plus </v-icon>
                {{ totalChargeUsed }}
              </v-card>
            </v-row>
            <v-row no-gutters>
              <v-card class="bg-background pa-2 rounded-lg flex-grow-1 text-caption" flat>
                <v-icon color="primary"> custom:offlineBolt </v-icon>
                {{ totalEnergy }}
                kWh needed to complete this trip
              </v-card>
            </v-row>
          </v-card-text>
        </v-card>
        <!-- Savings -->
        <v-card class="rounded-lg mb-3" color="transparent">
          <v-card-title style="font-size: 16px">
            <v-icon color="primary" class="mr-1"> mdi-piggy-bank-outline </v-icon>
            ${{ fuelSavings }}
            fuel savings
          </v-card-title>
        </v-card>
        <!-- emissions savings -->
        <v-card class="rounded-lg mb-3" color="transparent">
          <v-card-title style="font-size: 14px">
            <v-icon color="primary" class="mr-1" size="28"> custom:fireIcon </v-icon>
            {{ avoidedCO2 }}
            kg CO₂ emissions avoided
          </v-card-title>
        </v-card>
      </v-card>
      <!-- Conditionally rendered default section if no selected trip. -->
      <v-card flat v-else>
        <v-card-text class="px-0"> could not load trip stats </v-card-text>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import type { SalesLeadComparisonData } from '@/classes/sales_classes/salesLeadTypes'
import TripComparison from '@/classes/trip_classes/tripComparison'
import type Vehicle from '@/classes/vehicle_classes/vehicle'
import { CalcVsType, type State } from '@/store/store_types'
import getAssetSrc from '@/utils/getAssetSrc'
import getDirectusImgUrl from '@/utils/getDirectusImgUrl'
import { isHydrogen } from '@/utils/hydrogenUtils'
import { getNiceDuration } from '@/utils/timeUtils'
import { computed } from 'vue'
import { useStore } from 'vuex'

const { displayedComparisonData, paddingRight = true } = defineProps<{
  displayedComparisonData?: TripComparison | SalesLeadComparisonData
  paddingRight?: boolean
}>()

const store = useStore<State>()

const vehicle = computed<Vehicle | undefined>(() => {
  if (displayedComparisonData instanceof TripComparison) return displayedComparisonData.vehicle
  else
    return store.state.vehicles.find(
      (v) => v.directusId === displayedComparisonData?.vehicle_external_id,
    )
})

const niceDuration = (seconds: number): string => getNiceDuration(seconds || 0, 'short')

function getImageSrc() {
  if (vehicle.value?.imageSrcPaths.length) return vehicle.value.imageSrcPaths[0]
  if (vehicle.value?.evModel?.imageUUID) return getDirectusImgUrl(vehicle.value.evModel.imageUUID)
  return getSrc('car_images/placeholder_car.png')
}

function getSrc(partialFilePath: string): string {
  return getAssetSrc(partialFilePath)
}

// computed stats

const drivingTime = computed(() => {
  if (displayedComparisonData instanceof TripComparison)
    return niceDuration(displayedComparisonData.drivingTime)
  else if (displayedComparisonData) return niceDuration(displayedComparisonData.time)
  return 'unknown'
})

const drivingDistance = computed(() => {
  let meters = 0
  if (displayedComparisonData instanceof TripComparison)
    meters = displayedComparisonData.drivingDistance
  else if (displayedComparisonData) meters = displayedComparisonData.distance
  return Math.round(meters / 1000)
})

const acChargingTime = computed(() => {
  let chargingTime = 0
  if (displayedComparisonData instanceof TripComparison)
    chargingTime = displayedComparisonData.estimatedChargingTime.ac_charging_time
  else if (displayedComparisonData) chargingTime = displayedComparisonData.estimatedChargingTime
  return niceDuration(chargingTime)
})

const dcChargingTime = computed(() => {
  let chargingTime = 0
  if (displayedComparisonData instanceof TripComparison)
    chargingTime = displayedComparisonData.estimatedChargingTime.dc_charging_time
  else if (displayedComparisonData) chargingTime = displayedComparisonData.estimatedChargingTime
  return niceDuration(chargingTime)
})

const HydrogenChargingTime = computed(() => {
  let chargingTime = 0
  if (displayedComparisonData instanceof TripComparison)
    chargingTime = displayedComparisonData.estimatedChargingTime.dc_charging_time
  else if (displayedComparisonData) chargingTime = displayedComparisonData.estimatedChargingTime
  return niceDuration(chargingTime / 33.3)
})

const numberOfChargingStopsNeeded = computed(() => {
  if (displayedComparisonData instanceof TripComparison) {
    const numberOfChargesNeeded = displayedComparisonData.numberOfChargingStopsNeeded
    const lower = Math.floor(numberOfChargesNeeded)
    const higher = Math.ceil(numberOfChargesNeeded)

    if (lower === higher) return lower + ' charging stops required'
    return lower + ' - ' + higher + ' charging stops required'
  } else if (displayedComparisonData) return displayedComparisonData.estimatedNumberOfChargingStops
  return 'unknown'
})

const totalChargeUsed = computed(() => {
  let percentage = 0
  if (displayedComparisonData instanceof TripComparison)
    percentage = displayedComparisonData.totalChargeUsed * 100
  else if (displayedComparisonData)
    percentage = displayedComparisonData.estimatedPercentageCharged * 100
  return Math.round(percentage) + '% of battery capacity used'
})

const totalEnergy = computed(() => {
  if (displayedComparisonData instanceof TripComparison)
    return Math.round(displayedComparisonData.totalEnergy)
  else if (displayedComparisonData) return Math.round(displayedComparisonData.energy)
  return 'unknown'
})

const calcVs = computed(() => store.state.calcVs)
const petrolKmPerL = computed(() => store.state.petrolKmPerLitre)
const dieselKmPerL = computed(() => store.state.dieselKmPerLitre)
const fuelCostPerL = computed(() => {
  if (calcVs.value === CalcVsType.DIESEL) return store.state.dieselCostPerLitre
  return store.state.petrolCostPerLitre
})
const kmPerL = computed(() => {
  if (calcVs.value === CalcVsType.DIESEL) return dieselKmPerL.value
  return petrolKmPerL.value
})
const publicCostPerKW = computed(() => store.state.defaultPublicCostPerKWh)
const publicCostPerMin = computed(() => store.state.defaultCostPerMinDC)
const privateCostPerKW = computed(() => store.state.defaultHomeCostPerKWh)

const fuelSavings = computed(() => {
  if (displayedComparisonData instanceof TripComparison)
    return Math.round(
      displayedComparisonData.equivalentFuelCost({
        fuelCostPerLitre: fuelCostPerL.value,
        kmPerLitre: kmPerL.value,
      }) -
        Math.max(
          displayedComparisonData.fastChargingCost({
            publicChargingCost: publicCostPerKW.value,
            publicChargingCostPerMin: publicCostPerMin.value,
          }),
          displayedComparisonData.homeChargingCost(privateCostPerKW.value),
        ),
    ).toLocaleString()
  else if (displayedComparisonData)
    return Math.round(
      displayedComparisonData.estimatedEquivalentFuelCost -
        Math.max(
          displayedComparisonData.estimatedFastChargingCost,
          displayedComparisonData.estimatedSlowChargingCost,
        ),
    ).toLocaleString()
  return 'unknown'
})

const avoidedCO2 = computed(() => {
  if (displayedComparisonData instanceof TripComparison)
    return Math.round(
      displayedComparisonData.calcAvoidedCO2({
        calcVs: calcVs.value,
        petrolKmPerLitre: petrolKmPerL.value,
        dieselKmPerLitre: dieselKmPerL.value,
        hydrogenCO2PerKg: store.state.hydrogen_CO2_kg_per_kg,
        electricityCO2PerKWh: store.state.electric_CO2_kg_per_kWh,
        calcAsHydrogen: isHydrogen(displayedComparisonData.vehicle),
      }) || 0,
    )
  else if (displayedComparisonData) return displayedComparisonData.estimatedAvoidedCO2
  return 0
})

const chargingTimeToUse = computed<'DC' | 'AC' | 'H' | 'AC DC split'>(() => {
  if (vehicle.value?.fuelType === 'Hydrogen') return 'H'
  if (vehicle.value?.evNavPlugs?.some((p) => p.PowerType === 'DC')) return 'DC'
  if (
    vehicle.value?.evNavPlugs?.some(
      (p) =>
        p.PowerType === 'AC_1_PHASE' ||
        p.PowerType === 'AC_2_PHASE' ||
        p.PowerType === 'AC_2_PHASE_SPLIT' ||
        p.PowerType === 'AC_3_PHASE',
    )
  )
    return 'AC'
  return 'DC'
})
</script>
